import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { AppError } from '@/app/core/http/app-error.model';
import { toAppError } from '@/app/core/http/error-catalog';
import { DEFAULT_PAGE_SIZE, PageMeta } from '@/app/core/models/paged-result.model';
import { SettlementBonus } from '../models/settlement-query.model';
import { Settlement } from '../models/settlement.model';
import { SettlementQueryRepository } from '../services/settlement-query.repository';

export type SettlementDetailStatus = 'loading' | 'ready' | 'notFound' | 'error';
export type SettlementBonusesStatus = 'loading' | 'ready' | 'empty' | 'error';

const ERROR_SUBJECT = 'el detalle de la liquidación';
const BONUSES_ERROR_SUBJECT = 'los bonos de la liquidación';

const emptyPage = (size: number): PageMeta => ({
  page: 0,
  size,
  totalElements: 0,
  totalPages: 1,
});

@Injectable()
export class SettlementDetailStore {
  private readonly repository = inject(SettlementQueryRepository);

  private readonly _status = signal<SettlementDetailStatus>('loading');
  private readonly _settlement = signal<Settlement | null>(null);
  private readonly _bonusesStatus = signal<SettlementBonusesStatus>('loading');
  private readonly _bonuses = signal<readonly SettlementBonus[]>([]);
  private readonly _size = signal(DEFAULT_PAGE_SIZE);
  private readonly _pageMeta = signal<PageMeta>(emptyPage(DEFAULT_PAGE_SIZE));
  private readonly _error = signal<AppError | null>(null);

  private settlementId: number | null = null;
  private headerRequest?: Subscription;
  private bonusesRequest?: Subscription;

  readonly status = this._status.asReadonly();
  readonly settlement = this._settlement.asReadonly();
  readonly bonusesStatus = this._bonusesStatus.asReadonly();
  readonly bonuses = this._bonuses.asReadonly();
  readonly pageMeta = this._pageMeta.asReadonly();
  readonly error = this._error.asReadonly();
  readonly isLoadingBonuses = computed(() => this._bonusesStatus() === 'loading');

  load(settlementId: number): void {
    this.settlementId = settlementId;
    this.headerRequest?.unsubscribe();
    this._error.set(null);
    this._settlement.set(null);
    this._status.set('loading');

    this.headerRequest = this.repository.getById(settlementId).subscribe({
      next: (settlement) => {
        this._settlement.set(settlement);
        this._status.set('ready');
      },
      error: (cause: unknown) => {
        if (cause instanceof HttpErrorResponse && cause.status === 404) {
          this._status.set('notFound');
          return;
        }
        this._error.set(toAppError(cause, ERROR_SUBJECT));
        this._status.set('error');
      },
    });
    this.loadBonuses(0);
  }

  changeSize(size: number): void {
    this._size.set(size);
    this.loadBonuses(0);
  }

  loadBonuses(page: number): void {
    if (this.settlementId === null) return;
    this.bonusesRequest?.unsubscribe();
    this._bonusesStatus.set('loading');

    this.bonusesRequest = this.repository
      .listBonuses(this.settlementId, { page, size: this._size() })
      .subscribe({
        next: (result) => {
          this._bonuses.set(result.content);
          this._pageMeta.set(result.page);
          this._bonusesStatus.set(result.page.totalElements === 0 ? 'empty' : 'ready');
        },
        error: (cause: unknown) => {
          this._bonuses.set([]);
          this._pageMeta.set(emptyPage(this._size()));
          if (!this._error()) this._error.set(toAppError(cause, BONUSES_ERROR_SUBJECT));
          this._bonusesStatus.set('error');
        },
      });
  }
}
